/**
 * Shared Axios client factory for Beskar frontend services.
 *
 * Every module-specific API layer (api.js, grc.js, amidala.js, litesabre.js…)
 * uses the same three pieces of glue:
 *   1. Attach `Authorization: Bearer <token>` from localStorage.
 *   2. Attach `X-Tenant-ID` from `localStorage.currentTenantId` so backends
 *      keep writes scoped to the tenant the user has actively selected.
 *   3. On 401 responses, purge auth state and bounce to `/login`.
 *
 * Centralising this here means new services can spin up their own client
 * with one line and stay in lock-step with the rest of the app.
 */
import axios from 'axios';

/**
 * Build a tenant-aware Axios instance.
 * @param {string} baseURL - Fully-qualified base URL for the client.
 * @param {object} [options]
 * @param {boolean} [options.withCredentials=false] - Send cookies with requests.
 * @param {boolean} [options.redirectOn401=true] - Redirect to `/login` on 401.
 * @returns {import('axios').AxiosInstance}
 */
export function createTenantClient(baseURL, options = {}) {
  const { withCredentials = false, redirectOn401 = true } = options;

  const client = axios.create({
    baseURL,
    headers: { 'Content-Type': 'application/json' },
    withCredentials,
  });

  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const tenantId = localStorage.getItem('currentTenantId');
      if (tenantId) {
        config.headers['X-Tenant-ID'] = tenantId;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (redirectOn401 && error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    },
  );

  return client;
}

export default createTenantClient;
